import React, { useEffect, useState, useRef } from "react";
import { useLocation} from "react-router-dom";
import { api } from "../api/httpClient";
import { API_ENDPOINTS } from "../api/config";
import { getLocationSuggestions } from "../api";
import { useFormContext } from "../context/FormContext";
import SearchResults from "../pages/SearchResults";
import { registerSearch } from "../api/services/carService";
import { useCurrency } from "../context/CurrencyContext";

/**
 * LandingSearch
 *
 * Handles deep-linked ad landing URLs like:
 *   /car-rental/usa-florida/orlando-international-airport-mcp
 */

const extractEnvelope = (raw) => {
  let data = raw;
  if (Array.isArray(data) && data.length > 0) data = data[0];
  if (data?.data) data = data.data;
  if (data?.result) data = data.result;
  return data || null;
};

const splitDateTime = (value) => {
  if (!value) return ["", "10:00"];
  const [date, time] = String(value).split("T");
  return [date || "", time ? time.slice(0, 5) : "10:00"];
};

// Resolve "Name" + internal location_id -> "Name (GDSCODE)", same format
// formatLocationWithType's displayText uses for manual selection. Falls
// back to the plain name if no matching location/gdsCode is found.
const withGdsCode = async (name, locationId) => {
  if (!name) return "";
  try {
    const matches = await getLocationSuggestions(name);
    const match =
      (matches || []).find(
        (loc) => loc.location_id === locationId || loc.locationId === locationId,
      ) || (matches || [])[0];
    const gdsCode = match?.gdsCode;
    const country = match?.country;
    const withCode = gdsCode ? `${name} (${gdsCode})` : name;
    return country ? `${withCode}, ${country}` : withCode;
  } catch (err) {
    console.error("Failed to resolve GDS code for location:", name, err);
    return name;
  }
};

const mapToSearchData = async (envelope) => {
  if (!envelope) return null;

  // search_request has the ids/dates/age (same shape /availability/search
  // takes); meta_info (when present) has the human-readable location names
  // - same envelope shape a normal executed search returns.
  const sr = envelope.search_request || envelope;
  const meta = envelope.meta_info || {};

  const pickupCode = sr.pickup_location_id || "";
  const dropoffCode = sr.dropoff_location_id || pickupCode;
  const sameLocation = pickupCode === dropoffCode;

  const [pickupDate, pickupTime] = splitDateTime(sr.pickup_datetime);
  const [dropoffDate, dropoffTime] = splitDateTime(sr.dropoff_datetime);

  if (!pickupCode || !pickupDate || !dropoffDate) return null;

  const pickupLocation = await withGdsCode(meta.pickup_location_name, pickupCode);
  const dropoffLocation = sameLocation
    ? pickupLocation
    : await withGdsCode(meta.dropoff_location_name, dropoffCode);

  return {
    sameLocation,
    pickupLocation,
    dropoffLocation,
    pickupCode,
    dropoffCode,
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime,
    driverCountry: sr.residence || "US",
    driverAge: String(sr.age || "35"),
  };
};

const LandingSearch = () => {
  const location = useLocation();
  const [status, setStatus] = useState("loading"); // loading | error
  const [searchId, setSearchId] = useState("");
  const handledRef = useRef(false);
  const { selectedCurrency } = useCurrency();

  const {
    setPickup,
    setDropoff,
    setPickupGDSCode,
    setDropOffGDSCode,
    setPickupDate,
    setPickupTime,
    setDropoffDate,
    setDropoffTime,
    setSameLocation,
    setDriverCountry,
    setDriverAge,
    isTestMode,
    clearSearchSession,
  } = useFormContext();

  useEffect(() => {
    // Guard against double-invocation (e.g. React 18 StrictMode dev double-mount)
    if (handledRef.current) return;
    handledRef.current = true;

    const run = async () => {
      try {
        const response = await api.post(API_ENDPOINTS.LANDING_URL, {
          landing_url: location.pathname,
        });

        const searchData = await mapToSearchData(extractEnvelope(response));
        if (!searchData) {
          setStatus("error");
          return;
        }

        // Seed the "URL to return to" (used by the checkout-return flow)
        // with the landing path itself, since there's no /results?... URL
        // here to capture it from. Guarded like SearchResults.js does: never
        // overwrite a value that's already there.
        if (!sessionStorage.getItem("originalSearchURL")) {
          sessionStorage.setItem("originalSearchURL", location.pathname);
        }

        // HeroSection.js (the home page) wipes the form's context/localStorage
        // unless this flag is set - the same flag the manual "Search Cars"
        // submit handlers set right before navigating. Without this, going
        // back to Home after a landing-URL search clears the location/dates
        // the middleware just resolved.
        sessionStorage.setItem("returningFromSearch", "true");
        sessionStorage.removeItem("pageWasRefreshed");

        // Feed the resolved search straight into FormContext - the same
        // fields SearchResults.js would normally read out of the URL query
        // string. SearchResults.js already falls back to context when the
        // URL has no query params, so once these are set, rendering
        // <SearchResults /> below runs a normal search with nothing in the
        // address bar.
        setSameLocation(searchData.sameLocation);
        setPickup(searchData.pickupLocation);
        setDropoff(searchData.dropoffLocation);
        setPickupGDSCode(searchData.pickupCode);
        setDropOffGDSCode(searchData.dropoffCode);
        setPickupDate(searchData.pickupDate);
        setPickupTime(searchData.pickupTime);
        setDropoffDate(searchData.dropoffDate);
        setDropoffTime(searchData.dropoffTime);
        setDriverCountry(searchData.driverCountry);
        setDriverAge(searchData.driverAge);

        // SearchResults reads the display names from localStorage first,
        // so overwrite whatever a previous manual search left there.
        localStorage.setItem("pickup_location_name", searchData.pickupLocation);
        localStorage.setItem("dropoff_location_name", searchData.dropoffLocation);

        // Drop any leftover expiry timer from an earlier search, otherwise
        // the watcher in SearchResults flags this page expired immediately.
        clearSearchSession();

        // SearchResults only restores by search_id now, so register one here.
        const registerResponse = await registerSearch({
          pickup_location_id: searchData.pickupCode,
          dropoff_location_id: searchData.dropoffCode,
          pickup_datetime: `${searchData.pickupDate}T${searchData.pickupTime}:00`,
          dropoff_datetime: `${searchData.dropoffDate}T${searchData.dropoffTime}:00`,
          age: parseInt(searchData.driverAge, 10) || 35,
          residence: searchData.driverCountry,
          currency: selectedCurrency?.code || "USD",
          is_test: isTestMode,
        });
        if (!registerResponse?.search_id) {
          setStatus("error");
          return;
        }
        setSearchId(registerResponse.search_id);

        setStatus("ready");
      } catch (err) {
        console.error("Failed to resolve landing URL:", err);
        setStatus("error");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (status === "ready") {
    return <SearchResults searchIdOverride={searchId} />;
  }

  if (status === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4 bg-slate-50">
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 max-w-md">
          <h2 className="text-2xl font-black text-navy mb-4">Something went wrong</h2>
          <p className="text-slate-500 mb-8 leading-relaxed">
            We couldn't load results for this link. It might be moved or currently unavailable.
          </p>
          <a
            href="/"
            className="inline-block px-8 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors shadow-lg"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh] bg-slate-50">
      <div className="animate-pulse flex flex-col items-center gap-4">
        <div className="h-12 w-12 border-4 border-t-gray border-t-primary rounded-full animate-spin"></div>
        <p className="text-slate-500 font-bold tracking-tight">Finding the best deals for you...</p>
      </div>
    </div>
  );
};

export default LandingSearch;